export type Point = {
  x: number;
  y: number;
};

export type Size = {
  width: number;
  height: number;
};

export type CoverLayer = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/** Pixel rect of the object-fit: cover image layer inside the frame */
export function getCoverLayer(image: Size, box: Size): CoverLayer {
  if (!image.width || !image.height || !box.width || !box.height) {
    return { left: 0, top: 0, width: box.width, height: box.height };
  }

  const imageAspect = image.width / image.height;
  const boxAspect = box.width / box.height;

  if (boxAspect > imageAspect) {
    const width = box.width;
    const height = box.width / imageAspect;
    return {
      left: 0,
      top: (box.height - height) / 2,
      width,
      height,
    };
  }

  const height = box.height;
  const width = box.height * imageAspect;
  return {
    left: (box.width - width) / 2,
    top: 0,
    width,
    height,
  };
}

/** Map image-space % coords to frame % for object-fit: cover */
export function imageToFramePercent(point: Point, image: Size, box: Size): Point {
  const layer = getCoverLayer(image, box);
  const frameX = layer.left + (point.x / 100) * layer.width;
  const frameY = layer.top + (point.y / 100) * layer.height;

  return {
    x: (frameX / box.width) * 100,
    y: (frameY / box.height) * 100,
  };
}

/** Map frame click % coords back to image-space % for object-fit: cover */
export function containerToImage(point: Point, image: Size, box: Size): Point {
  if (!image.width || !image.height || !box.width || !box.height) {
    return point;
  }

  const layer = getCoverLayer(image, box);
  const frameX = (point.x / 100) * box.width;
  const frameY = (point.y / 100) * box.height;
  const layerX = frameX - layer.left;
  const layerY = frameY - layer.top;

  return {
    x: (layerX / layer.width) * 100,
    y: (layerY / layer.height) * 100,
  };
}

/** Map a screen click inside the frame to image-space % coords */
export function imagePercentFromClientPoint(
  clientX: number,
  clientY: number,
  frameRect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  image: Size
): Point {
  const box = { width: frameRect.width, height: frameRect.height };
  const layer = getCoverLayer(image, box);
  const layerX = clientX - frameRect.left - layer.left;
  const layerY = clientY - frameRect.top - layer.top;

  return {
    x: (layerX / layer.width) * 100,
    y: (layerY / layer.height) * 100,
  };
}

export const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

export const round = (value: number) => Math.round(value * 10) / 10;

export type CardPlacement = 'left' | 'right' | 'bottom' | 'top';

/** Pick card side opposite the marker so the panel does not cover the pin */
export function getCardPlacement(point: Point): CardPlacement {
  if (point.x >= 60) return 'left';
  if (point.x <= 40) return 'right';
  if (point.y <= 28) return 'bottom';
  if (point.y >= 72) return 'top';
  return point.x >= 50 ? 'left' : 'right';
}

export function getLineAnchorToCard(
  dotX: number,
  dotY: number,
  cardLeft: number,
  cardTop: number,
  cardWidth: number,
  cardHeight: number
): Point {
  const cardCenterX = cardLeft + cardWidth / 2;
  const cardCenterY = cardTop + cardHeight / 2;
  const dx = cardCenterX - dotX;
  const dy = cardCenterY - dotY;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return {
      x: dx > 0 ? cardLeft : cardLeft + cardWidth,
      y: cardTop + cardHeight * 0.22,
    };
  }

  return {
    x: cardCenterX,
    y: dy > 0 ? cardTop : cardTop + cardHeight,
  };
}
